import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdDevices, MdOutlineSecurity, MdOutlineLocalShipping, MdOutlineVerified, MdAutoAwesome, MdLogin } from 'react-icons/md';

export default function StudioNoirLayout({ p }) {
  const T = p.storeTheme || {};
  const buttonColor = T.buttonColor || "#121212"; 
  const primaryColor = T.primaryColor || "#121212";
  const secondaryColor = T.secondaryColor || "#737373"; 

  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Creative Portfolio & Concept Store",
    title: "Studio Noir. Pure Minimalism.",
    subtitle: "A high-contrast curated space showcasing bespoke clothing, handmade ceramics, and design monographs.",
    primaryCta: "Shop Collection",
    secondaryCta: "Read Manifesto",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafafa] text-[#121212] antialiased selection:bg-[#121212] selection:text-white" style={{ fontFamily: T.fontFamily || "Outfit, sans-serif" }}>
      {/* Stark top banner */}
      <div className="w-full bg-[#121212] py-2.5 px-4 text-center text-[10px] font-bold uppercase tracking-[0.3em] text-white">
        <span>
          STUDIO NOIR: Minimalist curation. Maximum expression. Free delivery on orders over $150.
        </span>
      </div>

      {/* Stark Clean Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur shadow-sm border-b border-neutral-200" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-neutral-900/10" size="h-9 w-9 rounded-none" />
            <div>
              <p className="text-sm font-black tracking-widest uppercase text-neutral-900">{p.storeInfo?.businessName}</p>
              <p className="text-[8px] text-neutral-500 font-bold tracking-[0.2em] uppercase">{p.storeInfo?.businessCategory || "Design Collective"}</p>
            </div>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Catalogue"],
              ["details", "Manifesto"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-[10px] font-bold tracking-[0.2em] uppercase transition-all hover:text-[#121212] ${
                  activePage === page 
                    ? "text-[#121212] border-b border-neutral-900 pb-1" 
                    : "text-neutral-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-48" />

            <button onClick={p.handleGoToWishlist} className="relative p-2 text-[#121212] hover:text-neutral-500 transition-colors" title="Wishlist">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-black rounded-full text-white flex items-center justify-center bg-neutral-900">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />

            {p.userMenuElement}

            <button 
              className="md:hidden p-2 text-neutral-900 hover:text-neutral-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#fafafa] border-b border-neutral-200 shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Catalogue"],
              ["details", "Manifesto"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-xs font-bold tracking-[0.15em] uppercase border-b border-neutral-100 ${
                  activePage === page ? "text-black font-black" : "text-neutral-500"
                }`}
              >
                {label}
              </button>
            ))}

            {/* Login Options for Mobile */}
            {!p.userProfile ? (
              <button
                onClick={() => {
                  p.handleOpenLogin();
                  setMobileMenuOpen(false);
                }}
                className="py-2 text-left text-xs font-bold tracking-[0.15em] uppercase border-b border-neutral-100 flex items-center gap-2 text-black"
              >
                <MdLogin className="text-lg" />
                Login / Sign Up
              </button>
            ) : (
              <div className="py-2 border-b border-neutral-100 flex flex-col gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-neutral-950 flex items-center justify-center text-xs font-bold text-white">
                    {p.userProfile?.name ? p.userProfile.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-neutral-900 uppercase tracking-widest">{p.userProfile?.name}</p>
                    <p className="text-[10px] text-neutral-500">{p.userProfile?.customer_mobile}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => {
                      p.handleViewProfile();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[9px] font-bold uppercase tracking-widest text-neutral-900 bg-neutral-100 hover:bg-neutral-200 px-3.5 py-1.5 transition-colors"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      p.handleViewOrders();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[9px] font-bold uppercase tracking-widest text-neutral-900 bg-neutral-100 hover:bg-neutral-200 px-3.5 py-1.5 transition-colors"
                  >
                    Orders
                  </button>
                  <button
                    onClick={() => {
                      p.handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[9px] font-bold uppercase tracking-widest text-white bg-red-600 hover:bg-red-700 px-3.5 py-1.5 transition-colors ml-auto"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Stark Asymmetrical Hero Header */}
            <section className="w-full max-w-6xl mx-auto px-6 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center text-left">
              <div className="flex flex-col items-start md:col-span-6 pr-0 md:pr-6">
                <span className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-neutral-500 mb-4">
                  <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                </span>
                <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#121212] leading-none mb-6 uppercase">
                  <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                </h1>
                <p className="text-xs md:text-sm text-neutral-500 font-medium leading-relaxed mb-9 max-w-md">
                  <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                </p>
                <div className="flex gap-4">
                  <button 
                    onClick={() => navigateTo("shop")}
                    className="px-8 py-3 rounded-none text-[10px] font-bold uppercase tracking-widest text-white bg-[#121212] hover:bg-neutral-800 transition-colors"
                  >
                    <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                  </button>
                  <button 
                    onClick={() => {
                      p.setActiveTab("details");
                      navigateTo("about");
                    }}
                    className="px-8 py-3 rounded-none text-[10px] font-bold uppercase tracking-widest border border-neutral-300 hover:border-black hover:text-black text-neutral-600 transition-all"
                  >
                    <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                  </button>
                </div>
              </div>

              {/* Asymmetric Hero Showcase */}
              <div className="w-full md:col-span-6 bg-neutral-100 p-2 md:p-3 shadow-sm border border-neutral-200/50">
                <HeroImage p={p} themeId="studionoir" className="h-[320px] md:h-[450px] overflow-hidden grayscale hover:grayscale-0 transition-all duration-700" />
              </div>
            </section>

            {/* Editorial Showcase */}
            <section className="border-y border-neutral-900/10 bg-white py-20">
              <div className="max-w-6xl mx-auto px-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16 border-b border-neutral-100 pb-8">
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-neutral-400">DESIGN PHILOSOPHY</span>
                    <h2 className="text-3xl font-bold tracking-tight text-neutral-900 mt-2 uppercase">Form Follows Purpose</h2>
                  </div>
                  <p className="max-w-md text-xs text-neutral-500 leading-relaxed">
                    We select our collections based on structural beauty, material integrity, and long-term utility. Every design holds a story.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-left">
                  {[
                    {
                      num: "01",
                      title: "Pure Materiality",
                      desc: "Sourcing organic cotton, high-fire stoneware, and natural oils directly from independent creators."
                    },
                    {
                      num: "02",
                      title: "Structural Rigor",
                      desc: "Clean geometric profiles designed to blend seamlessly into modern architectural environments."
                    },
                    {
                      num: "03",
                      title: "Monochrome Craft",
                      desc: "Emphasizing shade, shadow, and texture rather than temporary color trends."
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="flex flex-col justify-between border-l border-neutral-900/10 pl-6 py-2">
                      <div>
                        <span className="text-xs font-black tracking-widest text-neutral-400 block mb-6">{item.num}</span>
                        <h4 className="text-base font-extrabold text-neutral-900 uppercase tracking-widest">{item.title}</h4>
                        <p className="text-xs text-neutral-500 mt-3.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Value Indicators */}
            <section className="max-w-6xl mx-auto px-6 py-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { title: "Safe Store Policy", icon: <MdOutlineSecurity className="text-xl text-neutral-900" />, desc: "Encrypted payments & checkout" },
                  { title: "Limited Curation", icon: <MdOutlineVerified className="text-xl text-neutral-900" />, desc: "Authentic, small-batch releases" },
                  { title: "Insured Delivery", icon: <MdOutlineLocalShipping className="text-xl text-neutral-900" />, desc: "Shipped in climate-neutral box" },
                  { title: "Manifesto Support", icon: <MdDevices className="text-xl text-neutral-900" />, desc: "Direct channel with curators" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-start text-left p-2 border-l border-neutral-100">
                    <div className="p-2 bg-neutral-100 mb-4 rounded-none">{b.icon}</div>
                    <p className="text-xs font-extrabold text-neutral-900 uppercase tracking-widest">{b.title}</p>
                    <p className="text-[10px] text-neutral-500 mt-2 leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalogue Section */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-16 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-neutral-100 pb-6">
            <div className="text-left">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-neutral-400">ARCHIVE CATALOGUE</span>
              <h3 className="text-3xl font-bold text-neutral-900 mt-1 uppercase">Minimal Collections</h3>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-none border border-neutral-300 hover:border-black text-[10px] font-bold text-neutral-700 transition-colors uppercase tracking-widest"
              >
                <MdFilterList size={14} /> Filter
              </button>
              
              <div className="rounded-none border border-neutral-200 bg-white p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-5 py-2 rounded-none text-[10px] font-extrabold uppercase tracking-widest transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: buttonColor, color: "#fff" } : { color: "#737373" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} dark={false} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} dark={false} />
    </div>
  );
}
